import { Error, Query, Search } from '@carbon/icons-react'
import {
  Box,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
} from '@chakra-ui/react'
import { List as VirtualizedList, AutoSizer } from 'react-virtualized'
import 'react-virtualized/styles.css'
import useGlobalSearch from 'api/useGlobalSearch'
import useGlobalSearchAutocomplete from 'api/useGlobalSearchAutocomplete'
import { LoadingAnimation, Select } from 'components'
import { apps, appsList } from 'config/apps'
import { IModel } from 'interfaces/model.interface'
import { ISingleNavigationPage } from 'interfaces/navigationPage.interface'
import { useEffect, useRef, useState } from 'react'
import useThrottle from 'utils/useThrottle'
import { EntitySearchType } from 'api/types'
import { useSearchParams } from 'react-router-dom'
import { SearchResultsCount } from 'modules/GlobalSearchOverlay/SearchResultsCount'
import _ from 'lodash'
import GlobalEntitySection, {
  EntryAppsMapping2,
} from 'modules/GlobalSearchOverlay/GlobalEntitySection'
import { IApp } from 'interfaces/navigationApp.interface'

export type SearchCategoryT = 'diseases' | 'candidates'

const ExcludedPages = ['tweet', 'esc-2021-s-sov-tweet', 'easd-2021-s-sov-tweet']

const AutocompleteSection = ({
  setGlobalSearchString,
  searchCategory,
  searchString,
  focusedIndex,
}: {
  searchCategory: SearchCategoryT
  searchString: string
  setGlobalSearchString: (value: string) => void
  focusedIndex: number
}) => {
  const { data, isLoading } = useGlobalSearchAutocomplete(
    searchCategory,
    searchString
  )

  const autocompleteOptions = data ?? []

  const listRef = useRef<VirtualizedList>(null)

  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      listRef.current.scrollToRow(focusedIndex)
    }
  }, [focusedIndex])

  if (isLoading || autocompleteOptions.length === 0) return null

  const rowRenderer = ({
    index,
    key,
    style,
  }: {
    index: number
    key: string
    style: React.CSSProperties
  }) => (
    <Box
      key={key}
      style={style}
      p='10px'
      bg={index === focusedIndex ? 'gray.300' : 'white'}
      cursor='pointer'
      borderBottom='1px solid #e0e0e0'
      _hover={{ backgroundColor: 'gray.200' }}
      onClick={() => setGlobalSearchString(autocompleteOptions[index])}
    >
      {_.startCase(_.toLower(autocompleteOptions[index]))}
    </Box>
  )

  return (
    <Box
      h='200px'
      overflow='auto'
      position='absolute'
      borderRadius='6px'
      boxShadow='xl'
      mt='65px'
      w='100%'
      zIndex={1000}
      backgroundColor='gray4'
    >
      <AutoSizer disableWidth>
        {({ height }) => (
          <VirtualizedList
            ref={listRef}
            width={700}
            height={height || 200}
            rowCount={autocompleteOptions.length}
            rowHeight={40}
            rowRenderer={rowRenderer}
          />
        )}
      </AutoSizer>
    </Box>
  )
}

const SearchInput = ({
  setSearchTerm,
  searchTerm,
  setSearchType,
}: {
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  setSearchType: React.Dispatch<React.SetStateAction<string>>
  searchTerm: string
}) => {
  const [showOptions, setShowOptions] = useState(true)
  const [searchCategory, setSearchCategory] =
    useState<SearchCategoryT>('diseases')
  const [globalSearchString, setGlobalSearchString] = useState<string>('')
  const searchString = useThrottle(globalSearchString, 300)

  const { data: autocompleteOptions = [] } = useGlobalSearchAutocomplete(
    searchCategory,
    searchString
  )

  const [focusedIndex, setFocusedIndex] = useState<number>(-1)

  function handleSearch(value: string) {
    setShowOptions(false)
    setSearchTerm(value)
    setGlobalSearchString(value)
    setFocusedIndex(-1)
  }

  useEffect(() => {
    if (globalSearchString !== searchTerm) {
      setShowOptions(true)
    }
  }, [setShowOptions, globalSearchString, searchTerm])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showOptions || autocompleteOptions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIndex((prev) => (prev + 1) % autocompleteOptions.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIndex((prev) =>
        prev <= 0 ? autocompleteOptions.length - 1 : prev - 1
      )
    } else if (e.key === 'Enter') {
      if (focusedIndex >= 0) {
        e.preventDefault()
        handleSearch(autocompleteOptions[focusedIndex])
      } else {
        handleSearch(globalSearchString)
      }
    }
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <Flex w={'100%'} alignItems={'center'}>
        <Flex w='155px' h='100%' alignItems={'center'}>
          <Select
            height='62px'
            value={{
              label: _.capitalize(searchCategory),
              value: searchCategory,
            }}
            isMulti={false}
            isSearchable={false}
            onChange={(e: { label: string; value: SearchCategoryT } | null) => {
              e && setSearchCategory(e.value)
              e && setSearchType(e.value)
            }}
            options={['candidates', 'diseases'].map((option) => ({
              label: _.capitalize(option),
              value: option,
            }))}
            borderColor='gray3'
            color='gray3'
            backgroundColor='white'
          />
        </Flex>
        <Flex w='100%' flexDir={'column'} position={'relative'}>
          <InputGroup>
            <InputLeftElement height='100%' w='54px' pl='22px' pr='12px'>
              <Icon as={Search} boxSize='20px' height='100%' color='gray.600' />
            </InputLeftElement>
            <Input
              autoFocus
              pl='54px'
              placeholder='Search...'
              value={globalSearchString}
              border='1px'
              borderColor='gray3'
              shadow='sm'
              height='62px'
              fontSize='21px'
              fontWeight={500}
              backgroundColor='white'
              borderRadius='6px'
              _focus={{
                border: '1px solid black',
                shadow: 'none',
              }}
              _placeholder={{
                color: 'gray.600',
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setGlobalSearchString(e.target.value)
                setFocusedIndex(-1)
              }}
              onKeyDown={handleKeyDown}
              color='black'
              data-cy='global-search-input'
            />
          </InputGroup>
          {showOptions && (
            <AutocompleteSection
              searchCategory={searchCategory}
              searchString={searchString}
              setGlobalSearchString={handleSearch}
              focusedIndex={focusedIndex}
            />
          )}
        </Flex>
      </Flex>
    </div>
  )
}
type PageT = ISingleNavigationPage<any, IModel<any, any>>
type PagesT = Record<string, PageT>

function getAppMapped(app: string) {
  return EntryAppsMapping2[app as keyof typeof EntryAppsMapping2]
}

function getAppPagesToRender(
  data: EntitySearchType[] | undefined,
  pages: PagesT
) {
  if (!data) {
    return []
  }

  const manualMapping: Record<string, string> = {
    'Press Releases': 'News & Press Releases',
    News: 'News & Press Releases',
    'Supply and Price': 'Deals',
  }
  const pageList = Array.from(
    new Set(
      data.map((x) => {
        const apps = x.apps?.map(getAppMapped)
        return `${x.endpoint}+${apps?.join('+')}`
      })
    )
  )
    .map((endpoint) => {
      return Object.values(pages).find((page) => {
        const [_endpoint, ...apps] = endpoint.split('+')
        const appHasPage = apps.some((app) => (page as any)?.app?.includes(app))
        const shouldShow =
          page.model.endpoint === _endpoint && appHasPage && !!page.path?.length

        return shouldShow
      })
    })
    .filter((page) => page !== undefined)
    .filter((page) => !ExcludedPages.includes(page?.model?.endpoint ?? ''))
    .reduce(
      (acc, page) => {
        const _entityName = String(page?.model?.entityName)
        const entityName = manualMapping[_entityName] ?? _entityName
        if (!entityName) return acc

        if (!acc.some((group) => group.entityName === entityName)) {
          acc.push({ entityName, pages: [] })
        }
        const group = acc.find((group) => group.entityName === entityName)
        page && group?.pages.push(page)
        return acc
      },
      [] as {
        entityName: string
        pages: PageT[]
      }[]
    )
    .sort((a, b) => a.entityName.localeCompare(b.entityName))
  return pageList
}

export type allAppsWithPagesT = {
  [x: string]: PagesT
}
// For @Fabian to make sure we have the same pages on the FE and BE
// function getAllPlatformPages(allAppsWithPages:any){
//   function getAppPageData(appKey: string) {
//     const appData = Object.keys(allAppsWithPages[appKey]).map((_page) => {
//       const page = allAppsWithPages[appKey][_page]
//       const endpoint = (page as any)?.apiEndpoint
//       const model = page.model.endpoint
//       const views = page.views.map((view) => ({
//         base: view.airtableBase,
//         name: view.airtableName,
//       }))
//       return { endpoint, model, views }
//     })
//     return { [appKey]: appData }
//   }

//   const platformPages = Object.keys(allAppsWithPages).map((_app) =>
//     getAppPageData(_app)
//   )
//   return platformPages
// }

function addAppSlugToEachPage(pages: PagesT, app: IApp<any>) {
  const updated: any = {}
  for (const key of Object.keys(pages)) {
    updated[`${key}+${app.slug}`] = {
      ...pages[key],
      app: app?.slug ?? '',
      apiEndpoint: app.endpoint,
    }
  }
  return updated
}

const SearchResults = ({
  data,
  isLoading,
  searchTerm,
  searchType,
  isFetched,
  isError,
}: {
  data: EntitySearchType[] | undefined
  isLoading: boolean
  isFetched: boolean
  isError: boolean
  searchTerm: string
  searchType: string
}) => {
  const selectedApps = appsList

  const selectedAppObject = selectedApps.map((app) => apps[app])
  const appPages = selectedAppObject
    .map((appObject) => {
      const pages = appObject.pages
      if (Object.keys(pages).length === 0) return null
      return { [appObject.slug]: addAppSlugToEachPage(pages, appObject) }
    })
    .filter((appPage) => appPage !== null)

  const pages = selectedAppObject.map((appObject) =>
    addAppSlugToEachPage(appObject.pages, appObject)
  )

  const allAppsWithPages = Object.assign({}, ...appPages)
  const allPages = Object.assign({}, ...pages)

  const pageSectionToRender = getAppPagesToRender(data, allPages)

  if (isLoading)
    return (
      <Box>
        <LoadingAnimation height='371px' backgroundColor='#fafafa' />
      </Box>
    )
  if (!data?.length && isFetched) {
    return (
      <Flex
        h='50%'
        alignItems={'center'}
        justifyContent={'center'}
        flexDir={'column'}
        gap='12px'
      >
        <Query size={'50px'} />
        <Text fontSize={'xl'}>
          No Results for <b>{searchTerm}</b>
        </Text>
      </Flex>
    )
  }
  if (isError) {
    return (
      <Flex
        h='50%'
        alignItems={'center'}
        justifyContent={'center'}
        flexDir={'column'}
        gap='12px'
      >
        <Error size={'50px'} />
        <Text fontSize={'xl'}>Something went wrong!</Text>
      </Flex>
    )
  }

  return (
    <Flex w='100%' mx='auto'>
      {data && (
        <Tabs my='1rem' w='100%'>
          <TabList maxW='800px' flexWrap={'wrap'}>
            {pageSectionToRender.map((page, i) => {
              return (
                <Tab
                  _selected={{
                    borderColor: 'transparent',
                    fontWeight: 600,
                  }}
                  key={i}
                >
                  <Text whiteSpace={'nowrap'}>{page.entityName}</Text>
                </Tab>
              )
            })}
          </TabList>

          <TabPanels overflow={'auto'}>
            {pageSectionToRender.map((page, i) => {
              const endpoints = page.pages.reduce(
                (acc: string[], _page) => [...acc, _page.model.endpoint],
                []
              )
              const filteredData = data?.filter((x) => {
                const appHasPage = x.apps?.some((app) => {
                  const approvedPages = (page as any)?.pages
                  return approvedPages?.some(
                    (page: any) => page.app === getAppMapped(app)
                  )
                })
                return appHasPage && endpoints.includes(x.endpoint)
              })
              if (filteredData.length === 0) return null

              return (
                <TabPanel
                  key={i}
                  display={'block'}
                  justifyContent='space-between'
                >
                  <SearchResultsCount
                    searchString={searchTerm}
                    count={filteredData.length}
                  />
                  <Flex flexDir={'column'}>
                    <GlobalEntitySection
                      allAppsWithPages={allAppsWithPages}
                      page={page.pages[0]!}
                      data={filteredData}
                      searchString={searchTerm}
                      searchType={searchType}
                    />
                  </Flex>
                </TabPanel>
              )
            })}
          </TabPanels>
        </Tabs>
      )}
    </Flex>
  )
}

const AppsGlobalSearch = () => {
  const [queryParams] = useSearchParams()
  const searchString = queryParams.get('searchString')
  const [searchTerm, setSearchTerm] = useState<string>(searchString ?? '')
  const [searchType, setSearchType] = useState<string>('diseases')
  const { data, isError, isFetching, isFetched } = useGlobalSearch(
    searchTerm,
    searchType
  )

  const isSearching = Boolean(searchTerm)

  return (
    <Box
      bg='gray.50'
      h='100%'
      w='60%'
      borderRight='1px solid'
      borderColor={'gray3'}
      id='global-search-left-section'
    >
      <Flex maxH='100vh' h='100vh'>
        <VStack
          w='90%'
          mx='auto'
          alignItems='center'
          spacing={6}
          h={isSearching ? '100%' : '400px'}
          transform={isSearching ? 'translateY(50px)' : 'translateY(35vh)'}
          transition='transform 0.7s ease-in-out'
        >
          <Flex justifyContent='center' alignItems='center' mb='25px'>
            <Text fontSize='3xl' fontWeight='bold' color='black'>
              Search Across All Apps
            </Text>
          </Flex>
          <SearchInput
            setSearchTerm={setSearchTerm}
            searchTerm={searchTerm}
            setSearchType={setSearchType}
          />
          <Flex>
            <SearchResults
              data={data}
              isFetched={isFetched}
              isError={isError}
              isLoading={isFetching}
              searchTerm={searchTerm}
              searchType={searchType}
            />
          </Flex>
        </VStack>
      </Flex>
    </Box>
  )
}

export default AppsGlobalSearch
